import { supabase } from './supabase';

export type InsightMode = 'understand' | 'next_question' | 'reply_calmly';

export interface ConversationStarter {
  label: string;
  text: string;
}

export interface ConversationInsight {
  summary: string;
  possibleMeanings: string[];
  doNotAssume: string;
  conversationStarters: ConversationStarter[];
  tone: 'calm' | 'playful' | 'direct' | 'caring';
  mode: InsightMode;
  generatedAt: string;
}

export interface InsightInput {
  testId: string;
  questionText: string;
  expectedAnswer?: string;
  partnerAnswer?: string;
  responseTimeMs?: number;
  editCount?: number;
  answerLength?: number;
  mode: InsightMode;
}

export class InsightError extends Error {
  status?: number;
  code?: string;
  userMessage: string;
  constructor(userMessage: string, status?: number, code?: string, detail?: string) {
    super(detail || userMessage);
    this.userMessage = userMessage;
    this.status = status;
    this.code = code;
  }
}

async function toInsightError(error: any): Promise<InsightError> {
  const res: Response | undefined = error?.context;
  // Network / CORS / function unreachable
  if (!res || typeof (res as any).json !== 'function') {
    return new InsightError('Could not reach the server. Check your connection and try again.', undefined, 'network', error?.message);
  }
  let body: any = null;
  try { body = await res.clone().json(); } catch { /* not JSON */ }
  const status = res.status;
  const code = body?.code as string | undefined;
  const detail = [body?.error, body?.detail].filter(Boolean).join(' - ') || error?.message;

  if (status === 404 && !code) return new InsightError('The insight service is not deployed yet.', status, 'not_deployed', detail);
  if (status === 401) return new InsightError('Your session expired. Please sign in again.', status, code ?? 'auth', detail);
  if (code === 'not_found') return new InsightError('This session could not be found on your account.', status, code, detail);
  if (code === 'not_configured') return new InsightError('The AI mediator is not set up yet (missing API key).', status, code, detail);
  if (code === 'provider_failed') return new InsightError('The AI provider did not respond. Please try again in a moment.', status, code, detail);
  return new InsightError('Insight could not be generated right now. Your answer is still safe.', status, code, detail);
}

export async function generateConversationInsight(input: InsightInput): Promise<ConversationInsight> {
  const { data, error } = await supabase.functions.invoke('analyze-answer', {
    body: input,
  });
  if (error) throw await toInsightError(error);
  if (!data?.insight) throw new InsightError('The insight response was empty. Please try again.', 200, 'empty');
  return data.insight as ConversationInsight;
}
